import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Slider, InputNumber, Select, Progress, Card, Row, Col, Alert, Divider, Badge } from "antd";
import { InfoCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { formatCurrency } from "../../../helpers/retirementGoalCalculations";
import { rmdDistributionPeriods } from "../../../helpers/retirementGoalCalculations";

// Reusing some logic from EducationProjection headers
import futurProjectionImg from "../../../assets/images/up_down.png";
import currencyImg from "../../../assets/images/currency3.png";
import breakdownImg from "../../../assets/images/performance.png";

const { Option } = Select;

const Title = styled.h3`
  margin-bottom: 0.5em !important;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 600;
  font-size: ${props => props.level === 2 ? '30px' : props.level === 3 ? '24px' : '20px'};
`;

const Text = styled.span`
  color: ${props => props.type === 'secondary' ? 'rgba(0, 0, 0, 0.45)' : 'inherit'};
  font-weight: ${props => props.strong ? '600' : 'normal'};
`;

const Paragraph = styled.p`
  margin-bottom: 1em;
  color: ${props => props.type === 'secondary' ? 'rgba(0, 0, 0, 0.45)' : 'inherit'};
`;

const FlexSpace = styled.div`
  display: flex;
  flex-direction: ${props => props.direction === 'vertical' ? 'column' : 'row'};
  gap: ${props => props.size === 'middle' ? '16px' : '8px'};
  width: ${props => props.style && props.style.width ? props.style.width : 'auto'};
`;

const fadeInUp = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModernContainer = styled.div`
  ${fadeInUp}
  padding: 0;
  border-radius: 16px;
  width: 100%;
  font-family: 'Poppins', sans-serif;
  background: #fff;
  overflow: hidden;
  animation: fadeInUp 0.6s ease-out;

  /* Target only InputNumber with the .Input class to fix height issues */
  .ant-input-number.Input {
    height: 32px !important;
  }
  .ant-input-number.Input .ant-input-number-input {
    height: 30px !important;
  }
`;

const ModernHeader = styled.div`
  background: white;
  padding: 2rem;
  border-bottom: 2px solid #f0f2f5;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const HeaderIcon = styled.div`
  width: 70px;
  height: 70px;
  background: rgba(42, 61, 163, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2a3da3;
  font-size: 32px;
`;

const SectionTitle = styled(Divider)`
  margin: 40px 0 !important;
  .ant-divider-inner-text {
    font-size: 18px !important;
    font-weight: 600 !important;
    color: #495057 !important;
  }
`;

const SummaryCardRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 0 2rem;
  margin-bottom: 32px;
`;

const SummaryItem = styled.div`
  flex: 1;
  min-width: 280px;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 16px;
  transition: all 0.3s ease;
  border: 1px solid #f0f2f5;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0,0,0,0.02);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => props.color || "#2a3da3"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemLabel = styled(Text)`
  font-size: 13px;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ItemValue = styled(Title)`
  margin: 0 !important;
  font-weight: 800 !important;
  font-size: 32px !important;
  color: ${props => props.color || "#2a3da3"} !important;
`;

const StyledCard = styled(Card)`
  margin: 0 2rem 2rem 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  border: 1px solid #f0f2f5;
  overflow: hidden;

  .ant-card-head {
    background: #2a3da3;
    padding: 16px 24px;
    border: none;
  }
  .ant-card-head-title {
    color: white;
    font-weight: 700;
  }
`;

const LeverRow = styled(Row)`
  background: #fafafa;
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 1rem !important;
  align-items: center;
  border: 1px solid #f0f2f5;
  transition: all 0.3s ease;
  &:hover {
    background: #fff;
    border-color: #2a3da3;
    box-shadow: 0 4px 12px rgba(42, 61, 163, 0.08);
  }
`;

const ImpactBadge = styled(Badge)`
  .ant-badge-count {
    background: #e6f7ff;
    color: #1890ff;
    font-weight: 700;
    border: 1px solid #91d5ff;
  }
`;

const RetirementStrategyResults = ({
    payload,
    setPayload,
    taxInfo,
    taxInfoAllData,
    rateOfReturnData,
    assetsAssign = []
}) => {
    const [retireAge, setRetireAge] = useState(parseFloat(payload["Desired Retirement Age"]) || 65);
    const [annualBudget, setAnnualBudget] = useState(parseFloat(payload["Net Annual Post-Retirement Budget"]) || 0);
    const [monthlySavings, setMonthlySavings] = useState(parseFloat(payload["Your Commitment"]) || 0);
    const [targetAccount, setTargetAccount] = useState(payload["Target Account"] || "");

    const [depletionAge, setDepletionAge] = useState(null);
    const [projectedIncome, setProjectedIncome] = useState(0);
    const [coveragePercent, setCoveragePercent] = useState(0);
    const [impactMap, setImpactMap] = useState({ age: 0, budget: 0, savings: 0 });

    const lifeExpectancy = parseFloat(payload["Client Life Expectancy"]) || 90;
    const currentAge = parseFloat(payload["Current Age"]) || 45;

    const runProjection = (params) => {
        const { rAge, budget, savings } = params;
        const inflationInput = payload["Inflation rate for this goal"];
        const matchedInflation = taxInfo && taxInfo.find((item) => item.name === inflationInput);
        const inflationRate = matchedInflation ? (parseFloat(matchedInflation.value) || 0) / 100 : 0.03;

        const returnRate = (payload["Assumed Annual Investment Return (%)"]) || (rateOfReturnData && rateOfReturnData.staticPercentage);
        const annualReturnRate = returnRate !== undefined ? Number(returnRate) / 100 : 0.07;

        const ssUser = parseFloat(payload["Your Estimated Social Security"]) || 0;
        const ssSpouse = parseFloat(payload["Social Security - Spouse"]) || 0;
        const pension = parseFloat(payload["Pension"]) || 0;
        const rental = parseFloat(payload["Net Rental Income"]) || 0;

        let additionalIncomes = [];
        if (Array.isArray(payload["Annual Amount"])) {
            additionalIncomes = payload["Annual Amount"];
        } else if (payload["Annual Amount"]) {
            additionalIncomes = [payload["Annual Amount"]];
        }
        const sumAdditionalIncome = additionalIncomes.reduce((s, v) => s + (parseFloat(v) || 0), 0);

        const taxableBalance = parseFloat(payload["Taxable Accounts Balance (Brokerage, Savings)"]) || 0;
        const taxDeferredBalance = parseFloat(payload["Tax-Deferred Accounts Balance (Traditional 401k/IRA)"]) || 0;
        const taxFreeBalance = parseFloat(payload["Tax-Free Accounts Balance (Roth 401k/IRA)"]) || 0;

        const taxableContrib = parseFloat(payload["Current Taxable Accounts Annual Contribution"]) || 0;
        const taxDeferredContrib = parseFloat(payload["Current Tax-Deferred Accounts Annual Contribution"]) || 0;
        const taxFreeContrib = parseFloat(payload["Current Tax-Free Accounts Annual Contribution"]) || 0;

        const yearsToRetirement = Math.max(0, rAge - currentAge);
        const yearsInRetirement = Math.max(0, lifeExpectancy - rAge + 30);

        const growAsset = (start, contrib, years, rate) => {
            if (rate === 0) return start + (contrib * years);
            return start * Math.pow(1 + rate, years) + contrib * ((Math.pow(1 + rate, years) - 1) / rate);
        };

        let tStart = growAsset(taxableBalance, taxableContrib, yearsToRetirement, annualReturnRate);
        let dStart = growAsset(taxDeferredBalance, taxDeferredContrib + (savings * 12), yearsToRetirement, annualReturnRate);
        let rStart = growAsset(taxFreeBalance, taxFreeContrib, yearsToRetirement, annualReturnRate);

        let curDepAge = lifeExpectancy + 20;
        let fIncome = 0;

        for (let y = 0; y < yearsInRetirement; y++) {
            const age = rAge + y;
            const spendingNeed = budget * Math.pow(1 + inflationRate, y + yearsToRetirement);
            const fixed = (ssUser + ssSpouse + pension + rental + sumAdditionalIncome) * Math.pow(1 + inflationRate, y + yearsToRetirement);

            if (y === 0) fIncome = fixed;
            let need = Math.max(0, spendingNeed - fixed);

            let wT = Math.min(need, tStart);
            need -= wT;

            let rmd = 0;
            if (age >= 73) {
                const period = rmdDistributionPeriods[age] || 25;
                rmd = dStart / period;
            }
            let wD = Math.max(rmd, Math.min(need, dStart));
            need -= Math.min(wD, need);

            let wR = Math.min(need, rStart);
            need -= wR;

            tStart = (tStart - wT) * (1 + annualReturnRate);
            dStart = (dStart - wD) * (1 + annualReturnRate);
            rStart = (rStart - wR) * (1 + annualReturnRate);

            if (tStart <= 0 && dStart <= 0 && rStart <= 0) {
                curDepAge = age;
                break;
            }
        }

        return {
            depletionAge: curDepAge,
            projectedIncome: fIncome,
            coveragePercent: Math.min(100, Math.round((fIncome / (budget || 1)) * 100))
        };
    };

    useEffect(() => {
        const results = runProjection({ rAge: retireAge, budget: annualBudget, savings: monthlySavings });
        setDepletionAge(results.depletionAge);
        setCoveragePercent(results.coveragePercent);
        setProjectedIncome(results.projectedIncome);

        const impactAge = runProjection({ rAge: retireAge + 1, budget: annualBudget, savings: monthlySavings }).depletionAge - results.depletionAge;
        const impactBudget = runProjection({ rAge: retireAge, budget: annualBudget - 2400, savings: monthlySavings }).depletionAge - results.depletionAge;
        const impactSavings = runProjection({ rAge: retireAge, budget: annualBudget, savings: monthlySavings + 500 }).depletionAge - results.depletionAge;

        setImpactMap({ age: impactAge, budget: impactBudget, savings: impactSavings });
    }, [retireAge, annualBudget, monthlySavings]);

    useEffect(() => {
        setPayload(prev => ({
            ...prev,
            "Desired Retirement Age": retireAge,
            "Net Annual Post-Retirement Budget": annualBudget,
            "Your Commitment": monthlySavings,
            "Target Account": targetAccount
        }));
    }, [retireAge, annualBudget, monthlySavings, targetAccount, setPayload]);

    const liquidAccounts = assetsAssign.filter(a => a.listType === "liquid");
    const verdictColor = depletionAge >= lifeExpectancy ? "#51cf66" : (depletionAge > 80 ? "#ffa94d" : "#ff6b6b");

    return (
        <ModernContainer>
            <ModernHeader>
                <HeaderIcon>
                    <ClockCircleOutlined />
                </HeaderIcon>
                <div>
                    <Title level={3} style={{ margin: 0 }}>Retirement Results & Strategy Optimization</Title>
                    <Paragraph style={{ margin: 0, color: "#6c757d" }}>
                        Interactive solver to maximize your retirement solvency and close the income gap.
                    </Paragraph>
                </div>
            </ModernHeader>

            <SummaryCardRow>
                <SummaryItem color={verdictColor}>
                    <div style={{ width: 64, height: 64, flexShrink: 0 }}>
                        <img width={64} height={64} src={futurProjectionImg} alt="Depletion" />
                    </div>
                    <ItemDetails>
                        <ItemLabel>Projected Depletion Age</ItemLabel>
                        <ItemValue level={3} color={verdictColor}>Age {depletionAge}</ItemValue>
                        <Badge
                            status={depletionAge >= lifeExpectancy ? "success" : "error"}
                            text={depletionAge >= lifeExpectancy ? "Goal Met!" : `${lifeExpectancy - depletionAge}yr Shortfall`}
                        />
                    </ItemDetails>
                </SummaryItem>

                <SummaryItem color="#108ee9">
                    <div style={{ width: 64, height: 64, flexShrink: 0 }}>
                        <img width={64} height={64} src={breakdownImg} alt="Coverage" />
                    </div>
                    <ItemDetails>
                        <ItemLabel>Income Coverage</ItemLabel>
                        <ItemValue level={3} color="#108ee9">{coveragePercent}%</ItemValue>
                        <Badge count="Reliability" style={{ backgroundColor: '#108ee9' }} />
                    </ItemDetails>
                </SummaryItem>

                <SummaryItem color="#51cf66">
                    <div style={{ width: 64, height: 64, flexShrink: 0 }}>
                        <img width={64} height={64} src={currencyImg} alt="Income" />
                    </div>
                    <ItemDetails>
                        <ItemLabel>Monthly Income</ItemLabel>
                        <ItemValue level={3} color="#51cf66">{formatCurrency(projectedIncome / 12)}</ItemValue>
                        <Text type="secondary" style={{ fontSize: 11 }}>Target: {formatCurrency(annualBudget / 12)}</Text>
                    </ItemDetails>
                </SummaryItem>
            </SummaryCardRow>

            <SectionTitle orientation="left">Visual Analytics</SectionTitle>

            <StyledCard title="Income Reliability Gap">
                <div style={{ padding: "10px 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                        <Text strong>Monthly Income Reliability</Text>
                        <Text strong color={verdictColor}>{coveragePercent}% Covered</Text>
                    </div>
                    <Progress
                        percent={coveragePercent}
                        status={depletionAge >= lifeExpectancy ? "success" : "active"}
                        strokeColor={{ '0%': '#108ee9', '100%': '#52c41a' }}
                        strokeWidth={20}
                        showInfo={false}
                    />
                    <Row gutter={32} style={{ marginTop: 24 }}>
                        <Col span={8}>
                            <Text type="secondary">Target Monthly Income</Text>
                            <Title level={4}>{formatCurrency(annualBudget / 12)}</Title>
                        </Col>
                        <Col span={8}>
                            <Text type="secondary">Projected Base Income</Text>
                            <Title level={4}>{formatCurrency(projectedIncome / 12)}</Title>
                        </Col>
                        <Col span={8}>
                            <Text type="secondary">Monthly Shortfall</Text>
                            <Title level={4} style={{ color: "#ff6b6b" }}>{formatCurrency(Math.max(0, (annualBudget - projectedIncome) / 12))}</Title>
                        </Col>
                    </Row>
                </div>
            </StyledCard>

            <SectionTitle orientation="left">The Strategy Solver</SectionTitle>

            <StyledCard title="Interactive Levers (Instant Impact)">
                <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                    Adjust the levers below to see real-time updates to your retirement solvency age.
                </Paragraph>

                <FlexSpace direction="vertical" style={{ width: '100%' }} size="middle">
                    <LeverRow gutter={24}>
                        <Col span={5}>
                            <Text strong style={{ fontSize: 16 }}>Retire Later</Text>
                            <br />
                            <Text type="secondary">Current: Age {payload["Desired Retirement Age"] || 65}</Text>
                        </Col>
                        <Col span={10}>
                            <Slider
                                min={Math.max(0, currentAge)}
                                max={85}
                                value={retireAge}
                                onChange={setRetireAge}
                                trackStyle={{ backgroundColor: "#2a3da3" }}
                                handleStyle={{ borderColor: "#2a3da3", backgroundColor: "#fff" }}
                            />
                        </Col>
                        <Col span={5}>
                            <InputNumber
                                className="Input"
                                min={Math.max(0, currentAge)}
                                max={85}
                                value={retireAge}
                                onChange={setRetireAge}
                                style={{ width: '100%' }}
                                formatter={v => `Age ${v}`}
                                parser={v => v.replace(/Age\s?|[^\d.]/g, '')}
                            />
                        </Col>
                        <Col span={4}>
                            <ImpactBadge count={`+${(impactMap.age * 1).toFixed(1)}yrs Impact`} />
                        </Col>
                    </LeverRow>

                    <LeverRow gutter={24}>
                        <Col span={5}>
                            <Text strong style={{ fontSize: 16 }}>Spend Less</Text>
                            <br />
                            <Text type="secondary">{formatCurrency(payload["Net Annual Post-Retirement Budget"] / 12 || 0)} / mo</Text>
                        </Col>
                        <Col span={10}>
                            <Slider
                                min={0}
                                max={50000}
                                step={100}
                                value={Math.round(annualBudget / 12)}
                                onChange={v => setAnnualBudget(v * 12)}
                                trackStyle={{ backgroundColor: "#2a3da3" }}
                                handleStyle={{ borderColor: "#2a3da3", backgroundColor: "#fff" }}
                            />
                        </Col>
                        <Col span={5}>
                            <InputNumber
                                className="Input"
                                min={0}
                                max={50000}
                                value={Math.round(annualBudget / 12)}
                                onChange={v => setAnnualBudget(v * 12)}
                                style={{ width: '100%' }}
                                formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={v => v.replace(/\$\s?|(\,*)/g, '')}
                            />
                        </Col>
                        <Col span={4}>
                            <ImpactBadge count={`+${(impactMap.budget * 0.5).toFixed(1)}yrs Impact`} />
                        </Col>
                    </LeverRow>

                    <LeverRow gutter={24}>
                        <Col span={5}>
                            <Text strong style={{ fontSize: 16 }}>Save More</Text>
                            <br />
                            <Text type="secondary">{formatCurrency(payload["Your Commitment"] || 0)} / mo</Text>
                        </Col>
                        <Col span={10}>
                            <Slider
                                min={0}
                                max={30000}
                                step={50}
                                value={monthlySavings}
                                onChange={setMonthlySavings}
                                trackStyle={{ backgroundColor: "#2a3da3" }}
                                handleStyle={{ borderColor: "#2a3da3", backgroundColor: "#fff" }}
                            />
                        </Col>
                        <Col span={5}>
                            <InputNumber
                                className="Input"
                                min={0}
                                max={30000}
                                value={monthlySavings}
                                onChange={setMonthlySavings}
                                style={{ width: '100%' }}
                                formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={v => v.replace(/\$\s?|(\,*)/g, '')}
                            />
                        </Col>
                        <Col span={4}>
                            <ImpactBadge count={`+${(impactMap.savings * 0.2).toFixed(1)}yrs Impact`} />
                        </Col>
                    </LeverRow>
                </FlexSpace>

                <Alert
                    message={
                        <span style={{ fontWeight: 600 }}>
                            Strategy Outcome: With these adjustments, your assets last until Age {depletionAge}.
                            {depletionAge >= lifeExpectancy ? " (Target Met! ✅)" : " (Working towards gap closure)"}
                        </span>
                    }
                    type={depletionAge >= lifeExpectancy ? "success" : "info"}
                    showIcon
                    style={{ marginTop: 24, borderRadius: 12 }}
                />
            </StyledCard>

            <SectionTitle orientation="left">Implementation Plan</SectionTitle>

            <StyledCard title="Action Commitment">
                <Row gutter={48} align="middle">
                    <Col span={12}>
                        <Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12, fontWeight: 700 }}>Additional Monthly Savings Required</Text>
                        <Title level={2} style={{ margin: "8px 0 0 0", color: "#2a3da3" }}>
                            {formatCurrency(Math.max(0, monthlySavings - (parseFloat(payload["Your Commitment"]) || 0)))}
                        </Title>
                    </Col>
                    <Col span={12}>
                        <Text strong>Destination Account</Text>
                        <Select
                            style={{ width: '100%', marginTop: 8 }}
                            placeholder="Select Account"
                            value={targetAccount}
                            onChange={setTargetAccount}
                            size="large"
                        >
                            {liquidAccounts.map(acc => (
                                <Option key={acc.id} value={acc.assetName}>{acc.assetName}</Option>
                            ))}
                            <Option value="New Vanguard 401k (Tax-Deferred)">New Vanguard 401k (Tax-Deferred)</Option>
                            <Option value="New Roth IRA (Tax-Free)">New Roth IRA (Tax-Free)</Option>
                        </Select>
                        {targetAccount && (
                            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', color: '#1890ff' }}>
                                <InfoCircleOutlined />
                                <Text type="info" style={{ fontSize: 12 }}>
                                    {targetAccount.includes("Deferred") ? "Reduces your current taxable income." : "Allows for tax-free growth and withdrawals."}
                                </Text>
                            </div>
                        )}
                    </Col>
                </Row>
            </StyledCard>

            <div style={{ padding: "0 2rem 2rem 2rem", textAlign: "right" }}>
                <Text type="secondary" italic>
                    * Projection assumes a {parseFloat(payload["Assumed Annual Investment Return (%)"]) || (rateOfReturnData && rateOfReturnData.staticPercentage) || 7}% rate of return.
                </Text>
            </div>
        </ModernContainer>
    );
};

export default RetirementStrategyResults;
